// ** Modules
import Image from 'next/image';
import { Icon as IconBase, type IconProps as IconifyProps } from '@iconify/react';

// ** UI
import Box from '@mui/material/Box';

// ** Utils
import { iconMap } from './icons';

// ** Types
import type { IconName } from './icons';
import type { ReactElement } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { CustomIconColorProps, IconRenderProps } from './types';

export type IconProps = Omit<Partial<IconifyProps>, 'icon'> & {
	/**
	 * The icon to display. Supports three formats:
	 * 1. Internal name from iconMap (e.g., 'home', 'chevron')
	 * 2. Local SVG path (e.g., '/icons/logo.svg')
	 * 3. Iconify identifier (e.g., 'mdi:user', 'tabler:settings')
	 */
	name: IconName | string;

	/**
	 * Alternative text for accessibility, primarily used for local SVG path icons.
	 */
	alt?: string;

	/**
	 * Shorthand that sets both width and height to the same value.
	 * Explicit `width` / `height` props take precedence over `size`.
	 */
	size?: number | string;

	/**
	 * Custom styles using MUI's system properties.
	 */
	sx?: SxProps<Theme>;

	/**
	 * Behavioral flag for internal components.
	 */
	expanded?: boolean;

	/**
	 * Behavioral flag for internal components.
	 */
	active?: boolean;

	/**
	 * Custom color configuration object for multi-layer icons.
	 */
	customIconColor?: CustomIconColorProps;
};

/**
 * Generic icon component — the single entry point for all icons.
 */
export const Icon = ({
	name,
	alt = '',
	size,
	width,
	height,
	sx,
	expanded,
	active,
	customIconColor,
	...rest
}: IconProps): ReactElement => {
	// Determine final dimensions (Priority: width/height > size > default)
	const finalWidth = width ?? size ?? '1.5rem';
	const finalHeight = height ?? size ?? '1.5rem';

	// Type detection logic
	const isMappedIcon = typeof name === 'string' && name in iconMap;

	const isLocalPath = typeof name === 'string' && (name.startsWith('/') || name.endsWith('.svg'));

	return (
		<Box
			component="span"
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: finalWidth,
				height: finalHeight,
				flexShrink: 0,
				lineHeight: 0,
				'& > svg': {
					width: '100%',
					height: '100%',
					display: 'block'
				},
				...sx
			}}
		>
			{isMappedIcon ? (
				(() => {
					const Render = iconMap[name as IconName] as (props: IconRenderProps) => ReactElement;

					const renderProps: IconRenderProps = {
						width: '100%',
						height: '100%',
						expanded,
						active,
						...(customIconColor ? { customIconColor } : {}),
						...(rest as any)
					};

					return <Render {...renderProps} />;
				})()
			) : isLocalPath ? (
				<Image
					src={name}
					alt={alt}
					width={40}
					height={40}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'contain'
					}}
				/>
			) : (
				<IconBase
					{...rest}
					icon={name}
					width="100%"
					height="100%"
					style={{
						display: 'block',
						width: '100%',
						height: '100%',
						...(rest.style ?? {})
					}}
				/>
			)}
		</Box>
	);
};
